import { useState } from 'react';
import { Share2, Copy, Check, TreePine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: { btn: 'Share Tree', dialogTitle: 'Share Your Tree Progress', dialogDesc: 'Share how your Living Tree is growing. Messages stay private — only the tree\'s visual progress is visible.', copyBtn: 'Copy Link & Text', copied: 'Copied!', linkCopied: 'Link copied!', copyFailed: 'Failed to copy' },
  es: { btn: 'Compartir Árbol', dialogTitle: 'Comparte el Progreso de Tu Árbol', dialogDesc: 'Comparte cómo crece tu Árbol Viviente. Los mensajes son privados — solo el progreso visual es visible.', copyBtn: 'Copiar Enlace y Texto', copied: '¡Copiado!', linkCopied: '¡Enlace copiado!', copyFailed: 'Error al copiar' },
  fr: { btn: 'Partager l\'Arbre', dialogTitle: 'Partagez la Progression de Votre Arbre', dialogDesc: 'Partagez la croissance de votre Arbre Vivant. Les messages restent privés — seule la progression visuelle est visible.', copyBtn: 'Copier le Lien et le Texte', copied: 'Copié !', linkCopied: 'Lien copié !', copyFailed: 'Échec de la copie' },
  pt: { btn: 'Compartilhar Árvore', dialogTitle: 'Compartilhe o Progresso da Sua Árvore', dialogDesc: 'Compartilhe como sua Árvore Viva está crescendo. As mensagens permanecem privadas — apenas o progresso visual é visível.', copyBtn: 'Copiar Link e Texto', copied: 'Copiado!', linkCopied: 'Link copiado!', copyFailed: 'Falha ao copiar' },
  ar: { btn: 'شارك الشجرة', dialogTitle: 'شارك تقدم شجرتك', dialogDesc: 'شارك كيف تنمو شجرتك الحية. الرسائل تبقى خاصة — فقط التقدم المرئي يظهر.', copyBtn: 'نسخ الرابط والنص', copied: 'تم النسخ!', linkCopied: 'تم نسخ الرابط!', copyFailed: 'فشل النسخ' },
  ja: { btn: 'ツリーを共有', dialogTitle: 'ツリーの進捗を共有', dialogDesc: 'リビングツリーの成長を共有しましょう。メッセージはプライベートのまま — 視覚的な進捗のみ表示されます。', copyBtn: 'リンクとテキストをコピー', copied: 'コピー済み！', linkCopied: 'リンクをコピーしました！', copyFailed: 'コピー失敗' },
  ko: { btn: '트리 공유', dialogTitle: '트리 진행 상황 공유', dialogDesc: '리빙 트리의 성장을 공유하세요. 메시지는 비공개로 유지됩니다 — 시각적 진행 상황만 표시됩니다.', copyBtn: '링크 및 텍스트 복사', copied: '복사됨!', linkCopied: '링크가 복사되었습니다!', copyFailed: '복사 실패' },
  id: { btn: 'Bagikan Pohon', dialogTitle: 'Bagikan Kemajuan Pohon Anda', dialogDesc: 'Bagikan pertumbuhan Pohon Hidup Anda. Pesan tetap pribadi — hanya kemajuan visual yang terlihat.', copyBtn: 'Salin Link & Teks', copied: 'Disalin!', linkCopied: 'Link disalin!', copyFailed: 'Gagal menyalin' },
  hi: { btn: 'पेड़ साझा करें', dialogTitle: 'अपने पेड़ की प्रगति साझा करें', dialogDesc: 'दिखाएं कि आपका जीवित पेड़ कैसे बढ़ रहा है। संदेश निजी रहते हैं — केवल दृश्य प्रगति दिखती है।', copyBtn: 'लिंक और टेक्स्ट कॉपी करें', copied: 'कॉपी हो गया!', linkCopied: 'लिंक कॉपी हो गया!', copyFailed: 'कॉपी विफल' },
};

interface ShareTreeProgressProps {
  fruitCount: number;
}

export function ShareTreeProgress({ fruitCount }: ShareTreeProgressProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = lc(copy, language);

  if (!user || fruitCount === 0) return null;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const treeUrl = `${origin}/tree/${user.id}`;

  const shareText = `🌳 My Living Tree has ${fruitCount} ${fruitCount === 1 ? 'fruit' : 'fruits'} — each one is a message I chose not to keep inside. Watch it grow!`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${treeUrl}`);
      setCopied(true);
      toast.success(t.linkCopied);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t.copyFailed);
    }
  };

  const socialLinks = [
    {
      label: 'WhatsApp',
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${treeUrl}`)}`,
      color: 'bg-green-600 hover:bg-green-700',
    },
    {
      label: 'X (Twitter)',
      href: `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(treeUrl)}`,
      color: 'bg-foreground hover:bg-foreground/90',
    },
    {
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(treeUrl)}&quote=${encodeURIComponent(shareText)}`,
      color: 'bg-blue-600 hover:bg-blue-700',
    },
    {
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(treeUrl)}`,
      color: 'bg-blue-700 hover:bg-blue-800',
    },
  ];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-2 rounded-xl glass border-border/50"
        onClick={() => setOpen(true)}
      >
        <Share2 className="h-4 w-4" />
        {t.btn}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TreePine className="h-5 w-5 text-emerald-500" />
              {t.dialogTitle}
            </DialogTitle>
            <DialogDescription>
              {t.dialogDesc}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Preview text */}
            <div className="rounded-xl bg-muted/50 p-4 text-sm text-foreground">
              {shareText}
            </div>

            {/* Copy link */}
            <Button
              variant="outline"
              className="w-full gap-2 rounded-xl"
              onClick={handleCopy}
            >
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              {copied ? t.copied : t.copyBtn}
            </Button>

            {/* Social buttons */}
            <div className="grid grid-cols-2 gap-2">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-colors ${link.color}`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
